import { openOAuthSession } from '../../utilities/oauthBrowser';
import debug from '../../utilities/debug';
import { recorder } from '../../services/sessionTelemetry/recorder';
import * as LocalAuthentication from 'expo-local-authentication';
import { Alert, Platform } from 'react-native';
import { store } from '../index';
import {
  IS_LOGGED_IN,
  IS_LOGGED_OUT,
  SET_TOKEN,
  UPDATE_CREDENTIALS,
  SET_AUTH_CHALLENGE,
  CLEAR_ALL,
  SHOW_LOADER,
  HIDE_LOADER,
  SHOW_NOTIFICATION,
  SET_LOCKED,
} from '../constants';
import {
  cognitoSignIn,
  cognitoRespondToChallenge,
  cognitoForgotPassword,
  cognitoConfirmForgotPassword,
  cognitoSignUp,
  cognitoConfirmSignUp,
  cognitoResendConfirmationCode,
  cognitoRefreshSession,
  cognitoConfirmDevice,
  cognitoUpdateDeviceStatus,
  cognitoGetUser,
} from '../../utilities/cognitoAuth';
import config from '../../config';
import { apiPost, apiGet, apiPartnersPost, getFreshToken } from '../../utilities/apiClient';
import { SET_SETTINGS, SET_CURRENT_ACCOUNT } from '../constants';
import {
  storeTokens,
  clearTokens,
  getIdToken,
  getStoredTokens,
  getDeviceKeyFromAccessToken,
} from '../../utilities/tokenStore';
import { getBiometricCredentials, clearBiometricToken, refreshBiometricToken } from '../../utilities/biometricStore';
import { passkeyLogin, isPasskeySupported } from '../../utilities/passkeys';
import { SET_PASSKEY_PROMPT } from '../constants';
import { fetchAccounts, preferredSessionAccount } from './accounts';
import { fetchUser } from './user';
import { fetchProjects } from './projects';
import {
  clearPendingPartnerInvite,
  getPendingPartnerInvite,
} from '../../utilities/pendingPartnerInvite';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function decodeJwt(token: string): Record<string, any> {
  const base64Url = token.split('.')[1] || '';
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '=='.slice(0, (4 - (base64.length % 4)) % 4);
  return JSON.parse(atob(padded));
}

function getJwtExpiresIn(token: string, fallbackSeconds = 3600): number {
  try {
    const exp = Number(decodeJwt(token).exp);
    if (!Number.isFinite(exp)) return fallbackSeconds;
    return Math.max(1, exp - Math.floor(Date.now() / 1000));
  } catch {
    return fallbackSeconds;
  }
}

const FEDERATED_EXISTING_ACCOUNT_MESSAGE =
  'This email is already linked to an existing account. Sign in with your existing method (email/password or previous provider), then connect Apple Sign-In from Settings.';

const normalizeFederatedErrorMessage = (rawMessage: string) => {
  const msg = (rawMessage || '').trim();
  const lower = msg.toLowerCase();
  if (!msg) return 'Authentication failed.';
  if (
    /email.*(already|exists|in use)/i.test(msg) ||
    /usernameexistsexception|aliasexistsexception/i.test(msg)
  ) {
    return FEDERATED_EXISTING_ACCOUNT_MESSAGE;
  }
  if (
    /invalid user attributes/i.test(msg) &&
    /name/i.test(msg) &&
    /required/i.test(msg)
  ) {
    return 'Apple did not provide a required profile field (name). Please retry Apple Sign-In. If it continues, sign in with your existing method and link Apple from Settings.';
  }
  if (
    lower.includes('invalid auth') ||
    lower.includes('notauthorizedexception') ||
    lower.includes('not authorized') ||
    lower.includes('you are not authorized')
  ) {
    return 'Authentication succeeded, but account access was rejected. Try signing in with your existing method first, then connect Apple Sign-In in Settings.';
  }
  if (lower === 'invalid_request' || lower.includes('invalid_request')) {
    return 'Apple sign-in request failed. This email may already be linked to another sign-in method. Sign in with your existing method, then connect Apple Sign-In in Settings.';
  }
  return msg;
};

const dispatch = (action: any) => store.dispatch(action);
const showLoader = (msg = 'Loading...') => dispatch({ type: SHOW_LOADER, payload: msg });
const hideLoader = () => dispatch({ type: HIDE_LOADER });
export const showNotification = (type: string, title: string, message: string) =>
  dispatch({ type: SHOW_NOTIFICATION, payload: { type, title, message } });

// Signup context must survive every challenge until session provisioning.
type ChallengeContext = {
  referral?: string;
  partnerInviteCode?: string;
  partnerOptions?: PartnerOptions;
};
let pendingChallengeSession: ChallengeContext & {
  username: string;
  session: string;
  challengeName: string;
} = { username: '', session: '', challengeName: '' };

function continueAuthChallenge(
  thunkDispatch: any,
  username: string,
  result: { ChallengeName?: string; Session?: string },
  context: ChallengeContext,
) {
  const challenge = result.ChallengeName === 'NEW_PASSWORD_REQUIRED'
    ? 'FORCE_PASSWORD'
    : ['SMS_MFA', 'SOFTWARE_TOKEN_MFA', 'EMAIL_OTP'].includes(result.ChallengeName || '')
      ? 'MFA'
      : null;
  if (!challenge) return null;
  pendingChallengeSession = {
    ...context,
    username,
    session: result.Session || '',
    challengeName: result.ChallengeName!,
  };
  thunkDispatch({ type: SET_AUTH_CHALLENGE, payload: { type: challenge } });
  hideLoader();
  return { success: false, challenge };
}

// ─── Sign In ─────────────────────────────────────────────────────────────────

export const signIn = (email: string, password: string, partnerInviteCode?: string) => async (thunkDispatch: any) => {
  showLoader('Signing in...');
  const username = email.trim().toLowerCase();

  try {
    const result = await cognitoSignIn(username, password);

    const challenge = continueAuthChallenge(thunkDispatch, username, result, { partnerInviteCode });
    if (challenge) return challenge;

    if (result.AuthenticationResult) {
      return await bootstrapSession(
        thunkDispatch,
        username,
        result.AuthenticationResult,
        partnerInviteCode ? { partnerInviteCode } : {},
      );
    }

    hideLoader();
    return { success: false, message: 'Sign in incomplete.' };
  } catch (err: any) {
    hideLoader();
    const msg = err?.message || 'Sign in failed. Please try again.';
    // LoginScreen renders the same message inline below the password field; firing
    // a global toast here on top of that produces a duplicate that users misread as
    // a second, unrelated alert (e.g. about Google sign-in).
    return { success: false, message: msg };
  }
};

// ─── Confirm MFA ─────────────────────────────────────────────────────────────

export const confirmMFA = (code: string) => async (thunkDispatch: any) => {
  showLoader('Verifying...');
  try {
    const { username, session, challengeName, ...context } = pendingChallengeSession;
    const result = await cognitoRespondToChallenge(challengeName, session, username, code);
    if (result.AuthenticationResult) {
      return await bootstrapSession(
        thunkDispatch,
        username,
        result.AuthenticationResult,
        context,
      );
    }
    const challenge = continueAuthChallenge(thunkDispatch, username, result, context);
    if (challenge) return challenge;
    hideLoader();
    return { success: false, message: 'Verification incomplete.' };
  } catch (err: any) {
    hideLoader();
    const msg = err?.message || 'Invalid code.';
    showNotification('error', 'Verification Failed', msg);
    return { success: false, message: msg };
  }
};

// ─── Passkey Sign In ─────────────────────────────────────────────────────────

/**
 * "Sign in with a passkey" (ED-2799). The OS presents the discoverable passkeys
 * for app.eleplan.com, the backend verifies the assertion and brokers a Cognito
 * session, and the app bootstraps exactly like a password sign-in. The passkey
 * is itself the user-verified factor, so the post-sign-in lock is skipped the
 * same way device-ticket logins skip it.
 */
export const signInWithPasskey = (partnerInviteCode?: string) => async (thunkDispatch: any) => {
  showLoader('Signing in...');
  try {
    const result = await passkeyLogin();
    if (!result.success) {
      hideLoader();
      return { success: false, cancelled: result.cancelled === true, message: result.message };
    }
    const outcome = await bootstrapSession(thunkDispatch, result.username, result.authResult, {
      skipPasskeyLock: true,
      ...(partnerInviteCode ? { partnerInviteCode } : {}),
    });
    // A passkey sign-in IS an unlock: stamp unlocked_at so a refresh inside the
    // grace window does not immediately ask for the same passkey again.
    if (outcome?.success) thunkDispatch({ type: SET_LOCKED, payload: { show: false, inactive: false } });
    return outcome;
  } catch (err: any) {
    hideLoader();
    return { success: false, message: err?.message || 'Passkey sign-in failed. Please try again.' };
  }
};

/**
 * Right after a brand-new account is signed in, offer a passkey once. Users who
 * already hold one, and devices that cannot make one, never see the prompt.
 */
export function offerPasskeySetup(thunkDispatch: any, source: 'register' | 'invite') {
  try {
    if (!isPasskeySupported()) return;
    const user = store.getState().authentication?.user;
    if (Array.isArray(user?.passkeys) && user.passkeys.length > 0) return;
    thunkDispatch({ type: SET_PASSKEY_PROMPT, payload: { show: true, source } });
  } catch {
    // The prompt is a convenience; it must never interfere with sign-in.
  }
}

// ─── Biometric Sign In ───────────────────────────────────────────────────────

export const signInWithBiometrics = (partnerInviteCode?: string) => async (thunkDispatch: any) => {
  showLoader('Signing in...');
  try {
    // disableDeviceFallback: true so the only path through this prompt is a
    // real biometric scan. With the default, expo offers the device's PIN /
    // pattern as a fallback after a failed scan or cancel, which on Android
    // can be auto-satisfied by a recently unlocked phone — defeating the
    // purpose of the biometric gate. Users who can't or don't want to scan
    // cancel the prompt and use the email/password form on the same screen.
    // (ED-2045)
    const localAuth = await LocalAuthentication.authenticateAsync({
      promptMessage: Platform.OS === 'ios' ? 'Sign in with Face ID / Touch ID' : 'Sign in with biometrics',
      cancelLabel: 'Cancel',
      disableDeviceFallback: true,
    });
    if (localAuth.success !== true) {
      hideLoader();
      const cancelled = localAuth.error === 'user_cancel' || localAuth.error === 'system_cancel' || localAuth.error === 'app_cancel';
      const msg = cancelled ? 'Biometric sign-in was cancelled.' : 'Biometric authentication failed.';
      showNotification('error', 'Sign In Failed', msg);
      return { success: false, message: msg };
    }

    const credentials = await getBiometricCredentials();
    if (!credentials) {
      hideLoader();
      return { success: false, message: 'No biometric credentials found.' };
    }

    const refreshClientCandidates = Array.from(new Set(
      [credentials.clientId, config.cognito.appClientId].filter((id): id is string => !!id && id.trim().length > 0)
    ));
    if (refreshClientCandidates.length === 0) {
      refreshClientCandidates.push(config.cognito.appClientId);
    }

    let result: any = null;
    let resolvedClientId = refreshClientCandidates[0] || config.cognito.appClientId;
    let authFailure: any = null;
    for (const clientId of refreshClientCandidates) {
      try {
        result = await cognitoRefreshSession(
          credentials.refreshToken,
          clientId,
          credentials.deviceKey || undefined,
        );
        resolvedClientId = clientId;
        authFailure = null;
        break;
      } catch (attemptErr: any) {
        const attemptMsg: string = attemptErr?.message || '';
        const attemptCode: string = attemptErr?.code || '';
        const isAuthError = /NotAuthorizedException|InvalidParameterException|invalid.*(refresh|token)|(refresh|token).*(invalid|expired|revoked|not valid)|expired|revoked/i.test(
          `${attemptMsg} ${attemptCode}`
        );
        if (!isAuthError) {
          throw attemptErr;
        }
        authFailure = attemptErr;
      }
    }

    if (!result && authFailure) {
      throw authFailure;
    }

    if (!result.AuthenticationResult) {
      // Cognito rejected the stored secure refresh token. Clear only the token
      // (keep the enabled flag) so password sign-in can silently re-seed it.
      await clearBiometricToken();
      hideLoader();
      const msg = 'Your biometric session has expired. Please sign in with your password to refresh it, then try biometric sign-in again.';
      showNotification('error', 'Session Expired', msg);
      return { success: false, message: msg };
    }

    // Cognito refresh doesn't return a new RefreshToken — keep the existing one
    const authResult = {
      ...result.AuthenticationResult,
      RefreshToken: result.AuthenticationResult.RefreshToken || credentials.refreshToken,
    };

    return await bootstrapSession(thunkDispatch, credentials.username, authResult, {
      refreshClientId: resolvedClientId || config.cognito.appClientId,
      deviceKey: credentials.deviceKey,
      ...(partnerInviteCode ? { partnerInviteCode } : {}),
    });
  } catch (err: any) {
    hideLoader();
    const rawMsg: string = err?.message || '';
    // Treat any Cognito auth/token error as a stale credential — covers:
    // "Invalid Refresh Token", "Refresh Token has expired", "Token not valid",
    // "NotAuthorizedException", "InvalidParameterException", "revoked", etc.
    const isAuthError = /NotAuthorizedException|InvalidParameterException|invalid.*(refresh|token)|(refresh|token).*(invalid|expired|revoked|not valid)|expired|revoked/i.test(rawMsg);
    if (isAuthError) {
      await clearBiometricToken();
      const msg = 'Your biometric session has expired. Please sign in with your password to refresh it, then try biometric sign-in again.';
      showNotification('error', 'Session Expired', msg);
      return { success: false, message: msg };
    }
    const msg = rawMsg || 'Biometric sign in failed.';
    showNotification('error', 'Sign In Failed', msg);
    return { success: false, message: msg };
  }
};

// ─── Force Password Change ───────────────────────────────────────────────────

export const completeNewPassword = (newPassword: string) => async (thunkDispatch: any) => {
  showLoader('Updating password...');
  try {
    const { username, session, challengeName, ...context } = pendingChallengeSession;
    const result = await cognitoRespondToChallenge(challengeName, session, username, newPassword);
    if (result.AuthenticationResult) {
      const outcome = await bootstrapSession(thunkDispatch, username, result.AuthenticationResult, context);
      // Invited and chat-signup users reach here on their first ever sign-in.
      if (outcome?.success) offerPasskeySetup(thunkDispatch, 'invite');
      return outcome;
    }
    const challenge = continueAuthChallenge(thunkDispatch, username, result, context);
    if (challenge) return challenge;
    hideLoader();
    return { success: false, message: 'Password update incomplete.' };
  } catch (err: any) {
    hideLoader();
    const msg = err?.message || 'Could not update password.';
    showNotification('error', 'Password Update Failed', msg);
    return { success: false, message: msg };
  }
};

// ─── In-chat signup handoff ──────────────────────────────────────────────────
//
// Register-by-chat (#3): the marketing support bubble signed the visitor up and
// opened the app with ?signup_handoff=<ticket>. We redeem the one-time ticket
// for the Cognito NEW_PASSWORD_REQUIRED challenge, seed it exactly like a normal
// sign-in's force-password challenge, and let the caller route to
// ForcePasswordScreen where the user sets their own password. The temp password
// never touches the client — only the opaque challenge session does.
export const redeemSignupHandoff = (ticketId: string) => async (thunkDispatch: any) => {
  try {
    const res = await apiPost('/support/signup-redeem', { ticket_id: ticketId });
    if (!res?.success || !res.email || !res.session) {
      showNotification('error', 'Sign-in link expired', res?.message || 'This sign-in link is invalid or has expired. Please sign up again.');
      return { success: false };
    }
    // Redeem succeeded. If a DIFFERENT account is already signed in on this
    // browser, sign it out so the new account's force-change-password takes over
    // (the registrant intends to land in the account they just created). We do
    // this only AFTER a successful redeem, so a bogus ?signup_handoff= link can't
    // be used to force-log-out a real user. signOut() also clears
    // pendingChallengeSession, so seed it afterwards.
    if (store.getState()?.session?.authorized) {
      await thunkDispatch(signOut());
    }
    const username = String(res.email).toLowerCase();
    // Carry the partner attribution captured at in-chat signup (ED-2557) so
    // completeNewPassword can hand it to bootstrapSession — provisioning then
    // attributes the brand-new account exactly like the Register CTA path.
    const referral = typeof res.referral === 'string' && res.referral.trim() ? res.referral.trim() : undefined;
    pendingChallengeSession = { username, session: res.session, challengeName: 'NEW_PASSWORD_REQUIRED', referral };
    thunkDispatch({ type: SET_AUTH_CHALLENGE, payload: { type: 'FORCE_PASSWORD' } });
    return { success: true, email: username };
  } catch (err: any) {
    showNotification('error', 'Sign-in link error', err?.message || 'Could not complete sign-in. Please try again.');
    return { success: false };
  }
};

// ─── Forgot Password ─────────────────────────────────────────────────────────

export const forgotPassword = (email: string) => async () => {
  try {
    await cognitoForgotPassword(email);
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not send reset code.' };
  }
};

export const confirmForgotPassword = (
  email: string,
  code: string,
  newPassword: string
) => async () => {
  try {
    await cognitoConfirmForgotPassword(email, code, newPassword);
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not reset password.' };
  }
};

// ─── Federated Sign In (Cognito Hosted UI) ───────────────────────────────────

const signInWithFederatedProvider = (
  identityProvider: 'Google' | 'SignInWithApple',
  providerLabel: string,
  referral?: string,
  partnerInviteCode?: string,
) =>
  async (thunkDispatch: any) => {
  const { domain, appClientId, mobileCallbackScheme, webCallbackUrl } = config.cognito;
  if (!domain || !appClientId) {
    showNotification('error', 'Configuration', `${providerLabel} sign-in is not configured.`);
    return { success: false };
  }

  // Web platform can't open `com.eleplan.app://` schemes — the
  // popup tries to navigate to it and the browser fails with
  // "scheme does not have a registered handler" (ED-2332). Use the
  // HTTPS bridge URL instead; the lambda at that path postMessages
  // the code back to window.opener and the rest of this flow runs
  // unchanged. iOS/Android still use the native scheme because the
  // OS auth session intercepts it.
  const redirectUri = Platform.OS === 'web' ? webCallbackUrl : mobileCallbackScheme;
  const params = new URLSearchParams({
    identity_provider: identityProvider,
    redirect_uri: redirectUri,
    response_type: 'code',
    client_id: appClientId,
    scope: 'openid email profile phone aws.cognito.signin.user.admin',
  });
  const authUrl = `https://${domain}/oauth2/authorize?${params.toString()}`;

  try {
    const scheme = redirectUri.split('://')[0] + '://';
    const result = await openOAuthSession(authUrl, scheme);

    if (result.type !== 'success' || !result.url) {
      return { success: false, message: 'Sign-in was cancelled.' };
    }

    const qs = result.url.includes('?') ? result.url.slice(result.url.indexOf('?')) : '';
    const searchParams = new URLSearchParams(qs);
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const code = searchParams.get('code');

    if (error || !code) {
      const msg =
        error === 'access_denied'
          ? 'Access was denied. Please try again.'
          : normalizeFederatedErrorMessage(errorDescription || error || 'Authentication failed.');
      showNotification('error', `${providerLabel} Sign-In`, msg);
      return { success: false, message: msg };
    }

    showLoader('Signing in...');

    const tokenRes = await fetch(`https://${domain}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: appClientId,
        code,
        redirect_uri: redirectUri,
      }).toString(),
    });

    if (!tokenRes.ok) {
      hideLoader();
      showNotification('error', `${providerLabel} Sign-In`, 'Token exchange failed. Please try again.');
      return { success: false };
    }

    const tokens = await tokenRes.json();

    if (!tokens.id_token || !tokens.access_token) {
      hideLoader();
      showNotification('error', `${providerLabel} Sign-In`, 'Invalid token response from server.');
      return { success: false };
    }

    const decoded = decodeJwt(tokens.id_token);
    const username = decoded.email || decoded['cognito:username'] || '';

    debug.log('authentication', `${providerLabel} sign-in token decoded`, {
      provider: identityProvider,
      sub: decoded.sub,
      cognitoUsername: decoded['cognito:username'],
      email: decoded.email,
      hasIdentities: !!decoded.identities,
    });

    // Forward the optional partner referral so a brand-new federated signup is
    // attributed to the partner, exactly as the email/password path does.
    // bootstrapSession only provisions (and links the referral) for a genuinely
    // new account, so an existing user signing in with Google/Apple is unaffected.
    return await bootstrapSession(thunkDispatch, username, {
      IdToken: tokens.id_token,
      AccessToken: tokens.access_token,
      RefreshToken: tokens.refresh_token || '',
      ExpiresIn: tokens.expires_in || 3600,
    }, {
      ...(referral ? { referral } : {}),
      ...(partnerInviteCode ? { partnerInviteCode } : {}),
    });
  } catch (err: any) {
    hideLoader();
    const raw = err?.message || `${providerLabel} sign-in failed. Please try again.`;
    const msg = normalizeFederatedErrorMessage(raw);
    showNotification('error', `${providerLabel} Sign-In`, msg);
    return { success: false, message: msg };
  }
};

export const signInWithGoogle = (referral?: string, partnerInviteCode?: string) =>
  signInWithFederatedProvider('Google', 'Google', referral, partnerInviteCode);
export const signInWithApple = (referral?: string, partnerInviteCode?: string) =>
  signInWithFederatedProvider('SignInWithApple', 'Apple', referral, partnerInviteCode);

// ─── Sign Out ─────────────────────────────────────────────────────────────────

async function unbindWebPushForTerminalLogout(): Promise<void> {
  try {
    const { unsubscribeWebPush } = require('../../services/webPush');
    await unsubscribeWebPush({ reason: 'sign_out' });
  } catch {
    // Never block a terminal logout on push cleanup.
  }
}

function markWebPushSessionAuthenticated(): void {
  try {
    const { markWebPushSessionActive } = require('../../services/webPush');
    markWebPushSessionActive?.();
  } catch {
    // Native runtimes and unsupported browsers do not participate.
  }
}

export const signOut = () => async (thunkDispatch: any, getState: any) => {
  // Best-effort: unhook the browser's push subscription so the next user on
  // this device doesn't inherit the previous one's notifications. This is a
  // web-only no-op on native; Expo-token lifecycle hardening is tracked
  // separately. Done before CLEAR_ALL so the backend update call still has
  // session.account in scope.
  await unbindWebPushForTerminalLogout();

  // Wipe cached vault + snapshot keys for every project this session could
  // see. SecureStore outlives CLEAR_ALL and is gated by DEVICE biometrics,
  // not by account — on a shared device, the next person to sign in (a
  // family member on the same care plan, with their own enrolled biometric)
  // could otherwise unlock the previous user's vault without ever knowing
  // the vault password. Both clear functions were written for exactly this
  // call site ("e.g. on sign-out") and the re-derive friction on the next
  // sign-in is the documented cost of the zero-knowledge design. Read the
  // project ids BEFORE CLEAR_ALL empties the store.
  try {
    const projectIds = Object.keys(getState?.()?.projects?.list || {});
    if (projectIds.length) {
      const { clearVaultKey } = require('../../utilities/vaultCrypto');
      const { clearSnapshotKey } = require('../../utilities/snapshotCrypto');
      await Promise.all(projectIds.flatMap((id: string) => [
        clearVaultKey(id).catch(() => {}),
        clearSnapshotKey(id).catch(() => {}),
      ]));
    }
  } catch {
    // Key cleanup is defense-in-depth — never block sign-out on it.
  }

  await clearTokens();
  pendingChallengeSession = { username: '', session: '', challengeName: '' };
  thunkDispatch({ type: IS_LOGGED_OUT });
  thunkDispatch({ type: CLEAR_ALL });
};

// ─── Registration ────────────────────────────────────────────────────────────

export const signUp = (data: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
}) => async () => {
  showLoader('Creating account...');
  try {
    const username = data.email.trim().toLowerCase();

    // Pre-registration email check (mirrors web's checkEmail call)
    const emailCheck = await apiGet(`/users/check-email?email=${encodeURIComponent(username)}`);
    if (emailCheck?.success === false) {
      hideLoader();
      return { success: false, message: emailCheck?.message || 'This email cannot be used.' };
    }

    const phoneFormatted = data.phone
      ? `+1${data.phone.replace(/\D/g, '').replace(/^1/, '')}`
      : undefined;

    const result = await cognitoSignUp(username, data.password, {
      email: username,
      name: `${data.first_name.trim()} ${data.last_name.trim()}`.trim(),
      given_name: data.first_name.trim(),
      family_name: data.last_name.trim(),
      phone_number: phoneFormatted,
    });

    hideLoader();
    return {
      success: true,
      userSub: result.UserSub,
      email: username,
      first_name: data.first_name,
      last_name: data.last_name,
      password: data.password,
    };
  } catch (err: any) {
    hideLoader();
    // If user already exists with unconfirmed account, surface a clear message
    const code = err?.code || '';
    if (code === 'UsernameExistsException') {
      return { success: false, code, message: 'An account with this email already exists. Try signing in.' };
    }
    return { success: false, message: err?.message || 'Registration failed. Please try again.' };
  }
};

// Partner-specific provisioning input. When set on confirmSignUp /
// bootstrapSession, provisionNewUser flips accounts/users.partner=true,
// skips the regular customer-side Stripe-free-subscription bootstrap (the
// partner gets a coupon-discounted real Stripe sub later via redemption),
// and calls /partners/onboard with the captured branding + NPI fields so
// the new partner lands in 'pending' state for admin review.
export interface PartnerOptions {
  company_name: string;
  npi_number?: string;
  npi_data?: any;
  brand_primary?: string;
  brand_secondary?: string;
  brand_logo?: string;
}

export const confirmSignUp = (
  email: string,
  code: string,
  password: string,
  referral?: string,
  partnerOptions?: PartnerOptions,
) => async (thunkDispatch: any) => {
  showLoader('Verifying...');
  try {
    const username = email.trim().toLowerCase();
    await cognitoConfirmSignUp(username, code);

    // Immediately sign in after verification (same as web's confirmSignup flow).
    // Forward `referral` so provisioning can persist the partner-attribution tag
    // on the brand-new user (matches web's createUser(payload, accountId, referral)).
    const signInResult = await cognitoSignIn(username, password);
    if (signInResult.AuthenticationResult) {
      const outcome = await bootstrapSession(thunkDispatch, username, signInResult.AuthenticationResult, { referral, partnerOptions });
      if (outcome?.success) offerPasskeySetup(thunkDispatch, 'register');
      return outcome;
    }

    const challenge = continueAuthChallenge(thunkDispatch, username, signInResult, { referral, partnerOptions });
    if (challenge) return challenge;
    hideLoader();
    return { success: false, message: 'Sign in incomplete.' };
  } catch (err: any) {
    hideLoader();
    const msg = err?.message || 'Invalid verification code.';
    showNotification('error', 'Verification Failed', msg);
    return { success: false, message: msg };
  }
};

export const resendSignUp = (email: string) => async () => {
  try {
    await cognitoResendConfirmationCode(email.trim().toLowerCase());
    return { success: true };
  } catch (err: any) {
    return { success: false, message: err?.message || 'Could not resend code.' };
  }
};

// ─── Bootstrap ───────────────────────────────────────────────────────────────

// Provision a brand-new user who doesn't yet have a DB record.
// Covers both Google sign-up and native sign-up completing their first session.
// Mirrors the web's provisionInitialSession, minus Stripe (handled later on web).
//
// `referral` is the optional partner/discount attribution code collected on
// RegisterScreen and forwarded through confirmSignUp/bootstrapSession. The
// backend persists it on the new user as a HubSpot `referral_or_discount_code`
// attribute (see backend/api/client/controllers/users/create/index.js).
const provisionNewUser = async (
  canonicalId: string,
  email: string,
  firstName: string,
  lastName: string,
  referral?: string,
  partnerOptions?: PartnerOptions,
  phone?: string,
): Promise<boolean> => {
  // Partner registrations use a different provisioning path than regular
  // customers: accounts.partner=true, account_name = company name, no
  // Stripe free-tier subscription created here (the partner's customers
  // get coupon-discounted Stripe subs at redemption time, but the partner
  // themselves doesn't pay), and /partners/onboard fires at the end to
  // allocate the referral_base + monetary parent coupon and INSERT the
  // partners row in 'pending' status.
  const isPartner = !!partnerOptions?.company_name;
  const timeZoneFull = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneOffset = new Date().getTimezoneOffset();
  const timeParts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(new Date());
  const timeZoneAbbreviation = timeParts.find((part) => part.type === 'timeZoneName')?.value || timeZoneFull;
  const timeZone = {
    full: timeZoneFull,
    abbreviation: timeZoneAbbreviation,
    offset: timeZoneOffset,
  };
  const randomUsername = `user_${Math.random().toString(36).slice(2, 10)}`;

  showLoader('Setting up your account...');

  const createUserResult = await apiPost(`/users/create/${canonicalId}/${canonicalId}`, {
    user: {
      cognito_id: canonicalId,
      username: randomUsername,
      email,
      first_name: firstName,
      last_name: lastName,
      time_zone: timeZone,
      ...(phone ? { phone } : {}),
    },
    status: 'active',
    account_id: canonicalId,
    referral: referral || undefined,
    partner: isPartner,
  });
  debug.log('authentication', 'Provision createUser response', {
    success: !!createUserResult?.success,
    message: createUserResult?.message || null,
    has_referral: !!referral,
    is_partner: isPartner,
  });

  let userExists = !!createUserResult?.success || /exist/i.test(createUserResult?.message || '');
  let createUserRetry: any = null;
  if (!createUserResult?.success && !/exist/i.test(createUserResult?.message || '')) {
    // Some backend paths return a generic failure while the row may already exist.
    // Re-check by email before treating this as fatal.
    const userLookup = await apiGet(`/users/check-email-detail?email=${encodeURIComponent(email)}`);
    userExists = userLookup?.available === false;
    debug.log('authentication', 'Provision createUser fallback lookup', {
      available: userLookup?.available,
      cognito_id: userLookup?.cognito_id || null,
      account_id: userLookup?.account_id || null,
    });
    if (!userExists) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      createUserRetry = await apiPost(`/users/create/${canonicalId}/${canonicalId}`, {
        user: {
          cognito_id: canonicalId,
          username: randomUsername,
          email,
          first_name: firstName,
          last_name: lastName,
          time_zone: timeZone,
          ...(phone ? { phone } : {}),
        },
        status: 'active',
        account_id: canonicalId,
        referral: referral || undefined,
        partner: isPartner,
      });
      userExists = !!createUserRetry?.success || /exist/i.test(createUserRetry?.message || '');
      debug.log('authentication', 'Provision createUser retry response', {
        success: !!createUserRetry?.success,
        message: createUserRetry?.message || null,
      });
    }
    if (!userExists) {
      const existingEmailWithoutResolvedIdentity =
        userLookup?.available === false && !userLookup?.cognito_id;
      const createUserRawError = (createUserResult?.message || '').trim();
      const createUserRetryRawError = (createUserRetry?.message || '').trim();
      const combinedCreateUserError = [createUserRawError, createUserRetryRawError].filter(Boolean).join(' | ');
      if (existingEmailWithoutResolvedIdentity || /email.*(already|exists|in use)/i.test(combinedCreateUserError)) {
        showNotification('error', 'Sign In Failed', FEDERATED_EXISTING_ACCOUNT_MESSAGE);
      } else {
        showNotification('error', 'Setup Failed', normalizeFederatedErrorMessage(combinedCreateUserError || 'Could not create your account. Please try again.'));
      }
      return false;
    }
  }

  const createAccountResult = await apiPost(`/accounts/create/${canonicalId}`, {
    account: {
      account_id: canonicalId,
      cognito_id: canonicalId,
      // For partner registrations, account_name + company_name carry the
      // partner's company. The /partners/onboard call below uses
      // accounts.company_name to derive the referral_base, so this MUST
      // be set on the account row before onboard runs.
      account_name: isPartner
        ? (partnerOptions!.company_name || `${firstName} ${lastName}`.trim() || email)
        : `${firstName} ${lastName}`.trim() || email,
      ...(isPartner ? { company_name: partnerOptions!.company_name, partner: true } : {}),
    },
  });
  debug.log('authentication', 'Provision createAccount response', {
    success: !!createAccountResult?.success,
    message: createAccountResult?.message || null,
  });

  if (!createAccountResult?.success && !/exist/i.test(createAccountResult?.message || '')) {
    // Account creation can race with prior retries; validate by fetching accounts.
    const fallbackAccounts = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
    debug.log('authentication', 'Provision createAccount fallback fetchAccounts', {
      accounts_count: fallbackAccounts?.accounts?.length || 0,
      message: fallbackAccounts?.message || null,
    });
    if (!fallbackAccounts?.accounts?.length) {
      showNotification('error', 'Setup Failed', 'Could not initialize your workspace. Please try again.');
      return false;
    }
  }

  // ── Partner onboarding ───────────────────────────────────────────────────
  // For partner registrations, skip the customer-side referral linking
  // below — partners aren't redeeming a coupon, they're being granted a
  // referral_base. Call /partners/onboard which:
  //   - allocates a referral_base from company_name
  //   - creates the monetary parent Stripe coupon
  //   - INSERTs the partners row with status='pending'
  //   - posts a Slack ping to the admin approval queue
  // The partner then lands in PartnerPendingScreen via AppShell until an
  // admin approves them.
  if (isPartner) {
    try {
      const onboardBody: Record<string, any> = {};
      if (partnerOptions!.brand_primary) onboardBody.brand_primary = partnerOptions!.brand_primary;
      if (partnerOptions!.brand_secondary) onboardBody.brand_secondary = partnerOptions!.brand_secondary;
      if (partnerOptions!.brand_logo) onboardBody.brand_logo = partnerOptions!.brand_logo;
      if (partnerOptions!.npi_number) onboardBody.npi_number = partnerOptions!.npi_number;
      if (partnerOptions!.npi_data) onboardBody.npi_data = partnerOptions!.npi_data;
      const onboardResult = await apiPartnersPost(
        `/onboard/${canonicalId}/${canonicalId}`,
        onboardBody,
      );
      debug.log('authentication', 'Partner onboard response', {
        success: !!onboardResult?.success,
        already_onboarded: !!onboardResult?.payload?.alreadyOnboarded,
        referral_base: onboardResult?.payload?.partner?.referral_base || null,
      });
      if (!onboardResult?.success) {
        // Non-fatal: the user/account exist; an admin can manually
        // re-trigger onboard from the admin panel. Surface a soft warning
        // so the partner sees something happened, but let them through.
        showNotification('warning', 'Partner setup incomplete',
          'Your account is created — please contact support so we can finish setting up your partner workspace.');
      }
    } catch (err: any) {
      debug.error('Partner onboard threw', { err: err?.message });
    }
    // Fires once per real signup (provisionNewUser only runs for users with
    // no DB record). Captured by the claimed pre-login session when the
    // prelogin flag is on; a no-op otherwise.
    recorder.track('registration_completed', { segment: 'partner' });
    return true;
  }

  // Free-tier provisioning is intentionally NOT done here anymore.
  //
  // The legacy flow used to:
  //   1. Create a Stripe customer for every new account, paid or free.
  //   2. Create a $0 "free" Stripe subscription so accounts.subscription_id +
  //      accounts.product_id were populated and the entitlement resolver
  //      had something to read.
  //
  // Both of those are deprecated under the dual-billing model:
  //   - Apple-platform users never get a Stripe customer until they upgrade
  //     (or never, if they stay on Apple).
  //   - Free users no longer need a Stripe subscription at all. The
  //     entitlement resolver (backend/api/client/services/cache/account-metadata.js)
  //     falls back to the canonical default free plan when the account
  //     has no subscription, so a NULL accounts.subscription_id is the
  //     normal "free user" state.
  //   - Stripe customer creation moves to a lazy path: only created when
  //     the user actually upgrades to a paid Stripe plan (web/RN-Web only;
  //     iOS uses Apple IAP).
  //
  // See backend/api/billing/migrations/003_legacy_free_sub_cleanup.sql for
  // the one-time DB cleanup that removes the dummy free Stripe subs created
  // by this legacy block prior to its removal.

  // ─── Partner referral attachment (post-account-create) ────────────────────
  //
  // RegisterScreen pre-verifies the code via /partners/referral/verify
  // (public). Now that the account row exists and the user is authenticated,
  // attach it via /partners/referral/link so the backend can:
  //   1. Re-verify the coupon (server-trust, never client-trust).
  //   2. Set accounts.referrer = code.
  //   3. Mark the matching invites row 'registered'.
  //   4. Fire the partner_customer_linked SendGrid email when the parent
  //      coupon resolves to a real partner_cognito_id.
  //
  // Best-effort: a failure here does NOT roll back the account. The user can
  // still complete signup and the partner can re-attach the code later via
  // the Admin surface or the upgrade flow's discount field. We log enough to
  // diagnose without leaking the code value to crash analytics.
  if (referral) {
    // A failure here is SILENT to everyone who would care.
    //
    // The account is already created, so the customer sees an ordinary
    // successful signup — on the free tier, unattributed, with no partner
    // discount. The partner never gets the partner_customer_linked email, so
    // they do not know either. And every reason this fails is a one-time admin
    // state rather than a bug in the request: SELF_SERVE_DISABLED, a partner
    // still 'pending', the max_accounts seat cap reached, a default_product_id
    // that no longer resolves. Those are all recoverable — if anyone finds out.
    //
    // debug.log only reaches the console behind a settings flag, so nothing was
    // recoverable in practice. Emitting a telemetry action instead puts the
    // failure in app_session_events where it can be counted, alerted on, and
    // broken down by reason.
    //
    // Deliberately NOT the code value: the comment below has always been right
    // that it should not leak. errorCode is a fixed server vocabulary.
    let linkOk = false;
    let linkErrorCode: string | null = null;
    try {
      const linkRes = await apiPartnersPost(
        `/referral/link/${canonicalId}/${canonicalId}`,
        { code: referral }
      );
      linkOk = !!linkRes?.success;
      linkErrorCode = linkRes?.errorCode || null;
      debug.log('authentication', 'Provision link-referral response', {
        success: linkOk,
        partnerNotified: !!linkRes?.payload?.partnerNotified,
        errorCode: linkErrorCode,
      });
    } catch (err: any) {
      linkErrorCode = 'request_failed';
      debug.warn('Provision link-referral threw (non-fatal)', { message: err?.message });
    }
    try {
      recorder.track(linkOk ? 'partner_link_succeeded' : 'partner_link_failed',
        linkOk ? undefined : { reason: linkErrorCode || 'unknown' });
    } catch { /* instrumentation must never affect signup */ }
    if (linkOk) await clearPendingPartnerInvite(referral);
  }

  recorder.track('registration_completed', { segment: 'customer' });
  return true;
};

/**
 * Confirm + remember the device Cognito just registered, so tomorrow's
 * REFRESH_TOKEN_AUTH is accepted.
 *
 * The pool sets DeviceConfiguration, so every sign-in returns NewDeviceMetadata
 * and leaves an UNCONFIRMED device entry behind. Cognito rejects refresh from an
 * unconfirmed device with NotAuthorizedException, performRefresh reads that as a
 * definitive auth failure, handleAuthExpiry clears the tokens — and because the
 * refresh only runs once the 24h ID token has expired, the user experiences it
 * as being signed out every morning.
 *
 * It is awaited before tokens are stored, but remains non-throwing: confirmation
 * failure must not reject an otherwise valid sign-in, while a successful return
 * guarantees the stored refresh session is durable.
 */
const confirmDeviceForSession = async (accessToken: string, meta?: { DeviceKey?: string; DeviceGroupKey?: string }) => {
  if (!accessToken || !meta?.DeviceKey || !meta?.DeviceGroupKey) return;
  try {
    const { createDeviceVerifier } = require('../../utilities/cognitoDeviceSrp');
    const { deviceSecretVerifierConfig } = await createDeviceVerifier(meta.DeviceGroupKey, meta.DeviceKey);
    await cognitoConfirmDevice(accessToken, meta.DeviceKey, deviceSecretVerifierConfig);
    await cognitoUpdateDeviceStatus(accessToken, meta.DeviceKey, 'remembered');
    debug.log('authentication', 'Device confirmed + remembered');
  } catch (err: any) {
    // Never PHI, never the verifier, never the device key.
    debug.warn('Device confirmation failed — refresh may be rejected in 24h', { message: err?.message });
  }
};

type BootstrapSessionOptions = {
  skipPasskeyLock?: boolean;
  refreshClientId?: string;
  deviceKey?: string;
  referral?: string;
  partnerOptions?: PartnerOptions;
  /**
   * A pre-minted invitation reached through `/register?code=...`. This is
   * intentionally distinct from `referral`: generic referrals only provision
   * brand-new accounts, while this value asks the authenticated backend to
   * accept an email-bound invitation for an existing identity.
   */
  partnerInviteCode?: string;
};

type LoadedAccounts = {
  accounts?: any[];
  current?: any;
  primary?: any;
  [key: string]: any;
};

const invitationFailureMessage = (result: any): string => {
  switch (result?.errorCode) {
    case 'EMAIL_MISMATCH':
      return 'Sign in with the email address this invitation was sent to.';
    case 'OWN_ACCOUNT_REQUIRED':
      return 'We could not verify your personal account yet. Your invitation is saved so you can try again.';
    case 'PERSONAL_INVITE_REQUIRED':
      return 'This link is not a personal invitation. Ask the partner to send you a new invite.';
    case 'ALREADY_LINKED':
      return 'This Eleplan account is already connected to another partner.';
    case 'INVITE_ALREADY_ACCEPTED':
      return 'This invitation has already been accepted by another account.';
    case 'INVITE_NOT_AVAILABLE':
    case 'NOT_FOUND':
      return 'This invitation is no longer available. Ask the partner to send a new one.';
    case 'PARTNER_NOT_ACTIVE':
      return 'This partner is not currently accepting invitations.';
    default:
      return result?.message || 'We could not accept the invitation. Your sign-in still completed.';
  }
};

const isTerminalInvitationFailure = (result: any): boolean => {
  // EMAIL_MISMATCH is terminal for this identity, but not for the invitation:
  // keep it so the recipient can sign out and use the invited email instead
  // of having the exact handoff this flow protects silently disappear.
  // OWN_ACCOUNT_REQUIRED can also be caused by account-provisioning
  // replication lag, so a later restore should retry it.
  const retryableCodes = new Set([
    'UNAVAILABLE',
    'MINT_FAILED',
    'request_failed',
    'EMAIL_MISMATCH',
    'OWN_ACCOUNT_REQUIRED',
  ]);
  if (retryableCodes.has(result?.errorCode)) return false;
  return typeof result?.status === 'number' && result.status >= 400 && result.status < 500;
};

const personalAccountFrom = (accountList: any[], canonicalId: string) =>
  accountList.find((account: any) => account?.account_id === canonicalId) || null;

/**
 * Accept an explicit invitation against the authenticated identity's own
 * account. Never use `accounts.current`: it may be a family membership or a
 * partner-access customer, neither of which the invite recipient owns.
 */
const acceptPartnerInviteForSession = async (
  canonicalId: string,
  code: string,
  loaded: LoadedAccounts,
  identity: Record<string, any>,
): Promise<{ loaded: LoadedAccounts; accepted: boolean }> => {
  let accountState = loaded || {};
  let accountList = Array.isArray(accountState.accounts) ? accountState.accounts : [];
  let personalAccount = personalAccountFrom(accountList, canonicalId);

  if (!personalAccount) {
    const accountName = [identity.given_name, identity.family_name]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join(' ') || String(identity.email || 'My Eleplan Account').trim();
    const created = await apiPost(`/accounts/create/${canonicalId}`, {
      account: {
        account_id: canonicalId,
        cognito_id: canonicalId,
        account_name: accountName,
      },
    });
    if (!created?.success && !/exist/i.test(created?.message || '')) {
      debug.warn('Could not create personal account for pending partner invitation', {
        status: created?.status || null,
      });
      showNotification(
        'error',
        'Invitation not accepted',
        'You are signed in, but we could not prepare your personal account for this invitation. Please try again.',
      );
      return { loaded: accountState, accepted: false };
    }

    accountState = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any) || {};
    accountList = Array.isArray(accountState.accounts) ? accountState.accounts : [];
    personalAccount = personalAccountFrom(accountList, canonicalId);
    if (!personalAccount) {
      showNotification(
        'error',
        'Invitation not accepted',
        'You are signed in, but your personal account is not ready yet. Please try again.',
      );
      return { loaded: accountState, accepted: false };
    }
  }

  let linkResult: any;
  try {
    linkResult = await apiPartnersPost(
      `/referral/link/${canonicalId}/${canonicalId}`,
      { code, acceptance: 'existing_account_invite' },
    );
  } catch (err: any) {
    linkResult = { success: false, errorCode: 'request_failed', message: err?.message };
  }

  if (!linkResult?.success) {
    if (isTerminalInvitationFailure(linkResult)) {
      await clearPendingPartnerInvite(code);
    }
    try {
      recorder.track('partner_invite_accept_failed', { reason: linkResult?.errorCode || 'unknown' });
    } catch { /* instrumentation must never affect sign-in */ }
    showNotification('error', 'Invitation not accepted', invitationFailureMessage(linkResult));
    return { loaded: { ...accountState, current: personalAccount }, accepted: false };
  }

  await clearPendingPartnerInvite(code);
  try { recorder.track('partner_invite_accept_succeeded'); }
  catch { /* instrumentation must never affect sign-in */ }
  showNotification(
    'success',
    'Invitation accepted',
    linkResult?.payload?.balanceDue
      ? 'Your partner benefit is connected. Visit Billing to choose a plan and cover any remaining balance.'
      : 'Your Eleplan account is now connected to the partner who invited you.',
  );

  const refreshed = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
  if (refreshed?.accounts?.length) {
    return { loaded: refreshed, accepted: true };
  }
  return { loaded: { ...accountState, current: personalAccount }, accepted: true };
};

export const bootstrapSession = async (
  thunkDispatch: any,
  username: string,
  authResult: { IdToken: string; AccessToken: string; RefreshToken: string; ExpiresIn: number; NewDeviceMetadata?: { DeviceKey: string; DeviceGroupKey: string } },
  options: BootstrapSessionOptions = {}
) => {
  try {
    const refreshClientId = options.refreshClientId || config.cognito.appClientId || '';
    const deviceKey = authResult.NewDeviceMetadata?.DeviceKey
      || options.deviceKey
      || getDeviceKeyFromAccessToken?.(authResult.AccessToken)
      || '';

    // Confirmation is non-throwing, but it must finish before this session is
    // considered durable. Otherwise the first refresh can race an unconfirmed
    // Cognito device and be treated as a terminally invalid refresh token.
    await confirmDeviceForSession(authResult.AccessToken, authResult.NewDeviceMetadata);
    await storeTokens({
      idToken: authResult.IdToken,
      accessToken: authResult.AccessToken,
      refreshToken: authResult.RefreshToken,
      refreshClientId,
      deviceKey,
      username,
      expiresIn: authResult.ExpiresIn,
    });

    // Keep the biometric SecureStore in sync so Face ID never gets an expired token.
    // refreshBiometricToken is a no-op when biometrics is disabled.
    refreshBiometricToken(authResult.RefreshToken, refreshClientId, deviceKey).catch(() => {});

    const decoded = decodeJwt(authResult.IdToken);
    const canonicalId = decoded.sub as string;
    const cognitoUsername = (decoded['cognito:username'] as string) || username;

    thunkDispatch({ type: UPDATE_CREDENTIALS, payload: { username } });
    thunkDispatch({ type: SET_TOKEN, payload: authResult.IdToken });

    // Read before fetchAccounts, which overwrites session.account with the
    // server's pick (the personal account). See preferredSessionAccount.
    const lastSessionAccount = (store.getState().session as any)?.account;
    let accounts = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
    let accountList: any[] = accounts?.accounts || [];
    let currentAccount = accounts?.current || (Array.isArray(accountList) ? accountList[0] : null);

    // Closed account (self-service closure → status='closed'). Do NOT run the
    // re-provision self-heal below — that would recreate an account. Surface a
    // clear message and sign out so they land back on the login screen.
    if (accounts?.account_closed) {
      debug.log('authentication', 'Sign-in blocked — account is closed', { canonicalId });
      hideLoader();
      Alert.alert('Account closed', 'This account has been closed. Contact support to reopen it.');
      await store.dispatch(signOut() as any);
      return { success: false, message: 'account-closed' };
    }

    // ── Fallback 1: legacy Google_ username (pre-link-lambda accounts) ─────────
    const looksFederatedUsername = /^(Google_|SignInWithApple_)/.test(cognitoUsername);
    if ((!Array.isArray(accountList) || !accountList.length) && looksFederatedUsername && cognitoUsername !== canonicalId) {
      debug.log('authentication', 'Trying legacy federated username lookup', { cognitoUsername, canonicalId });
      const legacyResult = await store.dispatch(fetchAccounts(cognitoUsername, cognitoUsername) as any);
      if (legacyResult?.accounts?.length) {
        accounts = legacyResult;
        accountList = legacyResult.accounts;
        currentAccount = legacyResult.current || accountList[0];
      }
    }

    // ── Fallback 2: email-based lookup + provisioning ─────────────────────────
    if (!Array.isArray(accountList) || !accountList.length) {
      // An empty list is not the same answer as "we could not ask", and the
      // difference matters more here than anywhere else: everything below is
      // SELF-HEAL for a genuinely missing row — check-email, an email re-lookup,
      // and provisionNewUser. Running it because the backend 5xx'd can create a
      // SECOND account for a user who already has one, which in a care record is
      // a split history, not a cosmetic bug.
      //
      // fetchAccounts already draws the line (get-accounts answers 503 for a DB
      // failure precisely so it is not laundered into "you have no accounts").
      // This read it as "no accounts" and healed. Stop and let them retry.
      if ((accounts as any)?.transient) {
        debug.log('authentication', 'Sign-in account load failed transiently — not self-healing', { canonicalId });
        hideLoader();
        showNotification('error', 'Sign In Failed', "We couldn't reach your account just now. Please try again.");
        return { success: false, message: 'accounts-unavailable', transient: true };
      }

      const fallbackEmailFromUsername = typeof username === 'string' && username.includes('@')
        ? username.trim().toLowerCase()
        : '';
      let email = ((decoded.email as string) || fallbackEmailFromUsername || '').trim().toLowerCase();
      let firstName = (decoded.given_name as string) || '';
      let lastName = (decoded.family_name as string) || '';
      let fullName = (decoded.name as string) || '';
      let phone = (decoded.phone_number as string) || '';

      // Sign in with Apple can omit profile claims in the ID token in some paths.
      // Pull canonical attributes from Cognito's GetUser endpoint using access token.
      if ((!email || !email.includes('@')) || (!firstName && !lastName && !fullName) || !phone) {
        try {
          const cognitoUser = await cognitoGetUser(authResult.AccessToken);
          if (!email && cognitoUser?.email) email = String(cognitoUser.email).trim().toLowerCase();
          if (!firstName && cognitoUser?.givenName) firstName = String(cognitoUser.givenName).trim();
          if (!lastName && cognitoUser?.familyName) lastName = String(cognitoUser.familyName).trim();
          if (!fullName && cognitoUser?.name) fullName = String(cognitoUser.name).trim();
          if (!phone && cognitoUser?.phone) phone = String(cognitoUser.phone).trim();
        } catch (_) {
          // Non-fatal fallback; we continue with token-derived claims.
        }
      }

      if (email && email.includes('@')) {
        const ceRes = await apiGet(`/users/check-email-detail?email=${encodeURIComponent(email)}`);

        if (ceRes?.available === false && ceRes?.cognito_id) {
          // Email belongs to an existing DB user row. Prefer querying with the currently
          // authenticated cognito subject; this avoids false negatives when legacy rows
          // exist but account membership hasn't been created yet for this session subject.
          const lookupAccountId = ceRes?.account_id || canonicalId;
          debug.log('authentication', 'Found existing account by email', {
            cognito_id: ceRes.cognito_id,
            account_id: lookupAccountId,
          });
          const emailAccounts = await store.dispatch(fetchAccounts(lookupAccountId, canonicalId) as any);
          if (emailAccounts?.accounts?.length) {
            accounts = emailAccounts;
            accountList = emailAccounts.accounts;
            currentAccount = emailAccounts.current || accountList[0];
          } else if (emailAccounts?.transient) {
            // Same reasoning as the guard above: the else-branch below provisions.
            debug.log('authentication', 'Email account re-lookup failed transiently — not self-healing', { canonicalId });
            hideLoader();
            showNotification('error', 'Sign In Failed', "We couldn't reach your account just now. Please try again.");
            return { success: false, message: 'accounts-unavailable', transient: true };
          } else {
            // Self-heal whenever the email-lookup branch found no usable accounts
            // for THIS canonicalId — regardless of whether check-email is pointing at
            // a stale legacy row owned by a different cognito_id. The previous gate
            // (`ceRes.cognito_id === canonicalId || !ceRes?.account_id`) skipped the
            // provision step when an unrelated legacy user/account row owned the email,
            // leaving fresh Android registrations on test-cycle emails dead with
            // "No account found. Please contact support." (ED-2043). Provisioning is
            // idempotent: users/create has an inactive-row recovery path, and
            // accounts/create keys the new row by canonicalId so legacy rows don't
            // collide. Either way the retry loop below finds the freshly-bound row.
            debug.log('authentication', 'Email-lookup branch returned no accounts for this canonicalId, provisioning', {
              ceRes_cognito_id: ceRes.cognito_id,
              ceRes_account_id: ceRes?.account_id || null,
              canonicalId,
            });
            const nameParts = fullName.split(/\s+/).filter(Boolean);
            const resolvedFirstName = firstName || nameParts[0] || 'User';
            const resolvedLastName = lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

            const provisioned = await provisionNewUser(canonicalId, email, resolvedFirstName, resolvedLastName, options.referral, options.partnerOptions, phone || undefined);
            if (!provisioned) {
              hideLoader();
              return { success: false, message: 'provision-failed' };
            }

            for (let attempt = 1; attempt <= 12; attempt++) {
              await new Promise(r => setTimeout(r, 250 * attempt));
              const retryResult = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
              debug.log('authentication', 'Provision fetchAccounts retry', {
                attempt,
                accounts_count: retryResult?.accounts?.length || 0,
                message: retryResult?.message || null,
              });
              if (retryResult?.accounts?.length) {
                accounts = retryResult;
                accountList = retryResult.accounts;
                currentAccount = retryResult.current || accountList[0];
                break;
              }
              // This loop is waiting for replication lag on rows we just wrote.
              // A definitive rejection is not lag — the rows will never show up,
              // and eleven more requests against a 10 req/s aggregate throttle
              // only take the budget from everyone else.
              if (retryResult?.transient === false) break;
            }
          }
        } else if (
          ceRes?.available === true ||
          (ceRes?.available === false && !ceRes?.cognito_id)
        ) {
          // Brand-new user — provision user + account records
          debug.log('authentication', 'Provisioning new federated user', { email, has_referral: !!options.referral });
          const nameParts = fullName.split(/\s+/).filter(Boolean);
          const resolvedFirstName = firstName || nameParts[0] || 'User';
          const resolvedLastName = lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

          const provisioned = await provisionNewUser(canonicalId, email, resolvedFirstName, resolvedLastName, options.referral, options.partnerOptions, phone || undefined);
          if (!provisioned) {
            hideLoader();
            return { success: false, message: 'provision-failed' };
          }

          // Retry fetching accounts with backoff to handle DB replication lag
          for (let attempt = 1; attempt <= 12; attempt++) {
            await new Promise(r => setTimeout(r, 250 * attempt));
            const retryResult = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
            debug.log('authentication', 'Provision fetchAccounts retry', {
              attempt,
              accounts_count: retryResult?.accounts?.length || 0,
              message: retryResult?.message || null,
            });
            if (retryResult?.accounts?.length) {
              accounts = retryResult;
              accountList = retryResult.accounts;
              currentAccount = retryResult.current || accountList[0];
              break;
            }
            // See the identical loop above: a definitive rejection is not
            // replication lag, so stop rather than spending eleven more requests.
            if (retryResult?.transient === false) break;
          }
        }
      }
    }

    // An invitation received from a partner email is not a generic promo code:
    // it is a pending authenticated action. Accept it only after the normal
    // fallback above has provisioned any missing user/account rows. `referral`
    // is excluded because brand-new registration has already handled that value
    // in provisionNewUser; keeping the channels distinct preserves the rule
    // that an ordinary referral never mutates an existing account at sign-in.
    const storedInvite = !options.referral && !options.partnerInviteCode
      ? await getPendingPartnerInvite()
      : null;
    const partnerInviteCode = options.partnerInviteCode || storedInvite?.code || '';
    if (partnerInviteCode) {
      const accepted = await acceptPartnerInviteForSession(
        canonicalId,
        partnerInviteCode,
        { ...accounts, accounts: accountList, current: currentAccount },
        decoded,
      );
      accounts = accepted.loaded;
      accountList = Array.isArray(accounts?.accounts) ? accounts.accounts : accountList;
      currentAccount = personalAccountFrom(accountList, canonicalId)
        || accounts?.current
        || currentAccount;
    } else {
      const landing = preferredSessionAccount(accountList, currentAccount, lastSessionAccount);
      if (landing?.account_id && landing.account_id !== currentAccount?.account_id) {
        currentAccount = landing;
        thunkDispatch({ type: SET_CURRENT_ACCOUNT, payload: landing });
      }
    }

    if (!Array.isArray(accountList) || !accountList.length) {
      hideLoader();
      showNotification('error', 'Sign In Failed', 'No account found. Please contact support.');
      return { success: false, message: 'no-accounts' };
    }

    const accountId = currentAccount?.account_id;
    const userResult = await store.dispatch(fetchUser(canonicalId, accountId) as any);
    await store.dispatch(fetchProjects(accountId, canonicalId) as any);

    markWebPushSessionAuthenticated();
    thunkDispatch({ type: IS_LOGGED_IN });
    hideLoader();

    // If the user has any lock factor configured (passkeys OR PIN),
    // lock immediately after sign-in so they must verify their identity
    // (mirrors web behaviour). A lock screen is a lock screen — both
    // factors gate access the same way at runtime, so they should
    // gate sign-in the same way too.
    // Device-ticket logins skip this: the desktop side already required passkey verification
    // before generating the QR code, so re-locking on mobile is redundant and defeats the purpose.
    const loadedUser = userResult?.payload || store.getState().authentication?.user;
    const hasPasskeys = Array.isArray(loadedUser?.passkeys) && loadedUser.passkeys.length > 0;
    const hasPin = !!loadedUser?.pin;
    if ((hasPasskeys || hasPin) && !options.skipPasskeyLock) {
      dispatch({ type: SET_LOCKED, payload: { show: true, inactive: false } });
    }

    return { success: true, accounts };
  } catch (err: any) {
    hideLoader();
    const msg = normalizeFederatedErrorMessage(err?.message || 'Authentication failed.');
    showNotification('error', 'Sign In Failed', msg);
    return { success: false, message: msg };
  }
};

// ─── Log in with device ticket (QR scan from desktop) ──────────────────────────

export const loginWithDeviceTicket = (ticketId: string, accountId: string, cognitoId: string) => async (thunkDispatch: any) => {
  showLoader('Signing in...');
  try {
    const res = await fetch(`${config.apiGateway.endpoint}/auth/device-login/redeem/${encodeURIComponent(accountId)}/${encodeURIComponent(cognitoId)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticket_id: ticketId }),
    });
    const data = res.ok ? await res.json().catch(() => ({})) : {};
    if (!data?.success || !data?.refresh_token) {
      const msg = data?.message || (res.ok ? 'Invalid response' : 'Ticket expired or already used.');
      showNotification('error', 'Sign In Failed', msg);
      hideLoader();
      return { success: false, message: msg };
    }
    // Prefer id_token + access_token stored directly in the ticket (avoids Cognito
    // REFRESH_TOKEN_AUTH which fails when device tracking is enabled).
    // Fall back to Cognito refresh only when they are absent (legacy tickets).
    let authResult: { IdToken: string; AccessToken: string; RefreshToken: string; ExpiresIn: number };
    const ticketDeviceKey = typeof data.device_key === 'string'
      ? data.device_key
      : getDeviceKeyFromAccessToken?.(data.access_token || '') || '';
    if (data.id_token && data.access_token) {
      authResult = {
        IdToken: data.id_token,
        AccessToken: data.access_token,
        RefreshToken: data.refresh_token,
        ExpiresIn: getJwtExpiresIn(data.id_token),
      };
    } else {
      // Legacy path — ticket was created without id/access tokens (older web clients).
      const result = await cognitoRefreshSession(
        data.refresh_token,
        data.client_id || undefined,
        ticketDeviceKey || undefined,
      );
      if (!result.AuthenticationResult) {
        showNotification('error', 'Session Expired', 'Your session has expired. Please sign in again.');
        hideLoader();
        return { success: false };
      }
      authResult = {
        ...result.AuthenticationResult,
        RefreshToken: result.AuthenticationResult.RefreshToken || data.refresh_token,
      };
    }
    const decoded = decodeJwt(authResult.IdToken);
    const username = decoded.email || decoded['cognito:username'] || '';
    return await bootstrapSession(thunkDispatch, username, authResult, {
      skipPasskeyLock: true,
      refreshClientId: data.client_id || config.cognito.appClientId,
      deviceKey: ticketDeviceKey,
    });
  } catch (err: any) {
    hideLoader();
    const rawMsg: string = err?.message || '';
    // Cognito token errors (expired/invalid/revoked refresh token) should surface
    // as a user-friendly "Session Expired" message rather than the raw API string.
    const isAuthError = /NotAuthorizedException|InvalidParameterException|invalid.*(refresh|token)|(refresh|token).*(invalid|expired|revoked|not valid)|expired|revoked/i.test(rawMsg);
    if (isAuthError) {
      const msg = 'Your session has expired. Please sign in again.';
      showNotification('error', 'Session Expired', msg);
      return { success: false, message: msg };
    }
    showNotification('error', 'Sign In Failed', rawMsg || 'Could not sign in.');
    return { success: false };
  }
};

// ─── Restore session on app launch ───────────────────────────────────────────

export const restoreSession = () => async (thunkDispatch: any) => {
  try {
    const token = await getIdToken();
    if (!token) return { success: false };

    const decoded = decodeJwt(token);
    const canonicalId = decoded.sub as string;
    const username = decoded.email || decoded['cognito:username'] || '';

    // Refresh BEFORE the first call rather than relying on it happening inside
    // one. This used to be an empty if-block whose comment said apiClient would
    // handle it — which it does, but a refresh that fails transiently used to
    // hand back the EXPIRED token, and the resulting 401 is classified by
    // fetchAccounts as a definitive auth rejection (not transient, since it is
    // not a 5xx). That is how a connectivity blip at launch signed out a user
    // whose refresh token was good for another 89 days, once per 24h token
    // lifetime.
    //
    // Doing it here means a failure is visible as a failure: getFreshToken
    // returns '' and we keep the session and let the app retry, instead of
    // discovering it as a 401 that looks like "your session is invalid".
    const exp = decoded.exp as number;
    let sessionToken = token;
    if (Date.now() / 1000 > exp) {
      const refreshed = await getFreshToken();
      if (refreshed) sessionToken = refreshed;
      if (!refreshed) {
        // Could not reach the sign-in service. handleAuthExpiry has already
        // signed out if the refresh token was genuinely rejected; reaching here
        // means it was transient, so keep the session and let the app retry.
        return { success: false, transient: true };
      }
    }

    thunkDispatch({ type: UPDATE_CREDENTIALS, payload: { username } });
    thunkDispatch({ type: SET_TOKEN, payload: sessionToken });

    // Read before fetchAccounts, which overwrites session.account with the
    // server's pick (the personal account). See preferredSessionAccount.
    const lastSessionAccount = (store.getState().session as any)?.account;
    let accounts = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
    let accountList = accounts?.accounts || [];
    let currentAccount = accounts?.current || (Array.isArray(accountList) ? accountList[0] : null);

    // Closed account (status='closed') — surface it and stay logged out instead
    // of leaving a blank session. Same guard bootstrapSession applies to the
    // sign-in / federated paths.
    if (accounts?.account_closed) {
      Alert.alert('Account closed', 'This account has been closed. Contact support to reopen it.');
      await unbindWebPushForTerminalLogout();
      thunkDispatch({ type: IS_LOGGED_OUT });
      return { success: false, message: 'account-closed' };
    }

    const pendingInvite = await getPendingPartnerInvite();
    if (pendingInvite?.code) {
      const accepted = await acceptPartnerInviteForSession(
        canonicalId,
        pendingInvite.code,
        { ...accounts, accounts: accountList, current: currentAccount },
        decoded,
      );
      accounts = accepted.loaded;
      accountList = Array.isArray(accounts?.accounts) ? accounts.accounts : accountList;
      currentAccount = personalAccountFrom(accountList, canonicalId)
        || accounts?.current
        || currentAccount;
    } else {
      const landing = preferredSessionAccount(accountList, currentAccount, lastSessionAccount);
      if (landing?.account_id && landing.account_id !== currentAccount?.account_id) {
        currentAccount = landing;
        thunkDispatch({ type: SET_CURRENT_ACCOUNT, payload: landing });
      }
    }

    if (!Array.isArray(accountList) || !accountList.length) {
      // fetchAccounts conflates "no accounts" with "couldn't reach the server."
      // On a TRANSIENT failure (network / 5xx) keep the rehydrated session and
      // let the app refetch on the next interaction, rather than bouncing a
      // valid user to the login screen on a refresh. Only force logout on a
      // definitive result: a real auth rejection (401/403) or a genuinely empty
      // account list. (partner: "refresh → login screen, won't accept creds")
      if ((accounts as any)?.transient) {
        return { success: false, transient: true };
      }
      await unbindWebPushForTerminalLogout();
      thunkDispatch({ type: IS_LOGGED_OUT });
      return { success: false };
    }

    const accountId = currentAccount?.account_id;
    const userResult = await store.dispatch(fetchUser(canonicalId, accountId) as any);
    await store.dispatch(fetchProjects(accountId, canonicalId) as any);

    // Fetch system settings (maintenance mode, debug flags, feature flags)
    // Fire-and-forget — don't block session restore on a settings failure
    apiGet('/users/get-settings').then((res) => {
      if (res?.payload) {
        thunkDispatch({ type: SET_SETTINGS, payload: res.payload });
      }
    }).catch(() => {});

    markWebPushSessionAuthenticated();
    thunkDispatch({ type: IS_LOGGED_IN });

    // Re-lock if the user has any lock factor (passkeys or PIN) and
    // they didn't just unlock within the grace window. Mirrors the
    // same check bootstrapSession does after sign-in.
    //
    // Why this matters: the persisted `session.locked` flag is
    // intentionally stripped on rehydrate (see store/index.ts), so
    // without an imperative re-lock here a desktop-web refresh — or
    // native cold-launch — drops the lock entirely. That defeated
    // the lock screen until this guard.
    //
    // Grace window: 30s. Lets the user refresh a tab right after
    // unlocking without re-prompting (common during active use), but
    // tight enough that walking away and coming back forces re-auth.
    // unlocked_at is persisted across reloads (only `locked` is
    // stripped from the session bag — other fields ride through).
    const restoredUser = userResult?.payload || store.getState().authentication?.user;
    const hasRestoredPasskeys = Array.isArray(restoredUser?.passkeys) && restoredUser.passkeys.length > 0;
    const hasRestoredPin = !!restoredUser?.pin;
    const sessionState = store.getState().session as { unlocked_at?: number | null };
    const recentlyUnlocked = typeof sessionState.unlocked_at === 'number'
      && Date.now() - sessionState.unlocked_at < UNLOCK_GRACE_MS;
    if ((hasRestoredPasskeys || hasRestoredPin) && !recentlyUnlocked) {
      thunkDispatch({ type: SET_LOCKED, payload: { show: true, inactive: false } });
    }

    return { success: true };
  } catch {
    return { success: false };
  }
};

/**
 * Warm-link counterpart to restoreSession. If a signed-in user taps a partner
 * invitation, RootNavigator persists the code and dispatches this action so
 * the link is accepted immediately instead of waiting for another sign-in.
 */
export const acceptPendingPartnerInvite = () => async (): Promise<{ success: boolean }> => {
  try {
    const [token, pendingInvite] = await Promise.all([
      getIdToken(),
      getPendingPartnerInvite(),
    ]);
    if (!token || !pendingInvite?.code) return { success: false };

    const decoded = decodeJwt(token);
    const canonicalId = decoded.sub as string;
    if (!canonicalId) return { success: false };
    const accounts = await store.dispatch(fetchAccounts(canonicalId, canonicalId) as any);
    if (accounts?.account_closed) {
      showNotification('error', 'Invitation not accepted', 'This Eleplan account is closed. Contact support to reopen it.');
      return { success: false };
    }

    const accepted = await acceptPartnerInviteForSession(
      canonicalId,
      pendingInvite.code,
      accounts || {},
      decoded,
    );
    const current = accepted.loaded?.current
      || personalAccountFrom(accepted.loaded?.accounts || [], canonicalId);
    if (accepted.accepted && current?.account_id) {
      await store.dispatch(fetchProjects(current.account_id, canonicalId) as any);
    }
    return { success: accepted.accepted };
  } catch (err: any) {
    debug.warn('Warm partner invitation acceptance failed', { message: err?.message });
    showNotification(
      'error',
      'Invitation not accepted',
      'We could not accept the invitation right now. It is saved so you can try again.',
    );
    return { success: false };
  }
};

// Grace window for the restoreSession re-lock. A user who just unlocked
// shouldn't be asked to re-authenticate every time they refresh a tab
// during active use. 30s is short enough that walking away and coming
// back still re-prompts.
const UNLOCK_GRACE_MS = 30_000;
